import { fourEClient } from "./fourEClient";
import { followupE2EClient } from "./followupE2EClient";
import { runProfileExtractFlow } from "./profileExtractFlowV2";
import { build4EAnalyzePayload } from "../utils/build4EAnalyzePayload";
import { build4EFollowupPayload } from "../utils/build4EFollowupPayload";
import { setProfileContinuityStatus } from "../utils/profileContinuity";
import { extractDreamText, sanitizeDreamHtml } from "../utils/safeDreamHtml";

export const FOUR_E_SOURCE_BACKEND = "backend_4e";

const make4EError = (response, fallbackMessage) => {
  const error = new Error(response?.message || fallbackMessage || "Backend 4E nie zwrócił poprawnej odpowiedzi.");
  error.code = response?.code || "backend_4e_error";
  error.status = response?.status || null;
  error.response = response || null;
  return error;
};

const normalizeProfileStateForCache = (state = {}, pack = null) => {
  const buildOptin = state.buildOptin === true || state.build_optin === 1 || state.build_optin === "1";
  const aiUseOptin = buildOptin && (state.aiUseOptin === true || state.ai_use_optin === 1 || state.ai_use_optin === "1");
  return {
    explicitConsent: buildOptin || aiUseOptin,
    buildOptin,
    aiUseOptin,
    hasPack: buildOptin && Boolean(state.hasPack || state.hasData || state.has_data || pack),
    updatedAt: String(state.updatedAt || state.updated_at || ""),
    lastRebuiltAt: String(state.lastRebuiltAt || state.last_rebuilt_at || state.updatedAt || state.updated_at || ""),
    profile: pack && typeof pack === "object" ? pack : null,
    aadUser: state.aadUser || state.aad_user || "",
  };
};

const syncProfileConsentFromBackend = async () => {
  const response = await fourEClient.profileGet().catch(() => null);
  if (!response?.ok) return null;
  const status = setProfileContinuityStatus(normalizeProfileStateForCache(response.state, response.pack || null));
  return status;
};

const stripPlainDreamPayload = (dream = {}) => ({
  id: String(dream?.id || ""),
  title: String(dream?.title || "Sen bez tytułu"),
  date: String(dream?.date || ""),
  time: String(dream?.time || ""),
  emotions: Array.isArray(dream?.emotions) ? dream.emotions : [],
  symbols: Array.isArray(dream?.symbols) ? dream.symbols : [],
  saveMode: dream?.saveMode === "e2e" ? "e2e" : "plain",
});

const sanitizePayloadDraft = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  return {
    dreamId: String(payload.dreamId || ""),
    mode: payload.mode || null,
    model: payload.model || null,
    tier: payload.tier || null,
    question: payload.question || "",
    dream: stripPlainDreamPayload(payload.dream || {}),
    analysis: payload.analysis ? {
      source: payload.analysis.source || "",
      mode: payload.analysis.mode || "ai",
      model: payload.analysis.model || "",
      createdAt: payload.analysis.createdAt || "",
    } : null,
    profile: payload.profile ? {
      enabled: Boolean(payload.profile.enabled),
      reason: payload.profile.reason || "",
      hasTldr: Boolean(payload.profile.tldr),
      hasMotifs: Boolean(payload.profile.motifs),
      hasEmotions: Boolean(payload.profile.emotions),
      hasTimeline: Boolean(payload.profile.timeline),
      hasSamples: Boolean(payload.profile.samples),
    } : null,
    profileGate: payload.profileGate || null,
    profileSnapshotMeta: payload.profileSnapshotMeta || null,
    profileUsedExpected: Boolean(payload.profileUsedExpected),
    client: payload.client || null,
  };
};

const normalizeBackendAnalysis = ({ response, payload, mode, tier }) => {
  const raw = response?.analysis || response;
  if (!response?.ok || !raw?.html) return null;

  const html = sanitizeDreamHtml(raw.html);
  const plainText = raw.plainText || extractDreamText(html);
  const normalizedTier = raw.tier || raw.modelTier || response?.meta?.model || payload?.model || tier || "fast";

  return {
    source: FOUR_E_SOURCE_BACKEND,
    mode: raw.mode || mode,
    tier: normalizedTier,
    modelTier: normalizedTier,
    model: raw.model || "backend-4e",
    html,
    plainText,
    createdAt: raw.createdAt || new Date().toISOString(),
    profileUsed: Boolean(raw.profileUsed),
    profileVersion: raw.profileVersion ?? payload?.profileGate?.version ?? null,
    profileSnapshotMeta: raw.profileSnapshotMeta || payload?.profileSnapshotMeta || null,
    profileGate: payload?.profileGate || null,
    promptBand: raw.promptBand || "",
    payloadDraft: sanitizePayloadDraft(payload),
    backendMeta: {
      profileExtractTicket: response?.profileExtractTicket || "",
      usage: response?.usage || null,
      meta: response?.meta || null,
      profileExtract: null,
      backendError: null,
    },
  };
};

const normalizeBackendFollowup = ({ response, payload, question }) => {
  const raw = response?.followup || response;
  if (!response?.ok || !raw?.a) return null;

  return {
    source: FOUR_E_SOURCE_BACKEND,
    q: String(raw.q || question || ""),
    a: String(raw.a || ""),
    used: true,
    createdAt: raw.createdAt || new Date().toISOString(),
    profileUsed: Boolean(raw.profileUsed),
    profileGate: payload?.profileGate || null,
    payloadDraft: sanitizePayloadDraft(payload),
    backendMeta: {
      usage: response?.usage || null,
      meta: response?.meta || null,
      backendError: null,
    },
  };
};

export const run4EAnalysisFlow = async ({ dream, mode, tier = "fast" }) => {
  if (!fourEClient.isConfigured()) {
    throw make4EError(
      { code: "not_configured", message: "Backend 4E nie jest skonfigurowany." },
      "Backend 4E nie jest skonfigurowany.",
    );
  }

  await syncProfileConsentFromBackend();

  const payload = await build4EAnalyzePayload({ dream, mode, tier });
  const response = await fourEClient.analyzeDream(payload);
  const backendAnalysis = normalizeBackendAnalysis({ response, payload, mode, tier });

  if (!backendAnalysis) {
    throw make4EError(response, "Backend 4E nie zwrócił poprawnej analizy.");
  }

  await syncProfileConsentFromBackend();

  const profileExtract = await runProfileExtractFlow({
    dream,
    analysis: backendAnalysis,
    ticket: response?.profileExtractTicket,
    backendExtract: response?.profileExtract,
  });
  backendAnalysis.backendMeta.profileExtract = profileExtract;

  return {
    ok: true,
    source: FOUR_E_SOURCE_BACKEND,
    analysis: backendAnalysis,
    payload: sanitizePayloadDraft(payload),
    response,
    profileExtract,
  };
};

export const run4EFollowupFlow = async ({ dream, analysis, question }) => {
  if (!fourEClient.isConfigured()) {
    throw make4EError(
      { code: "not_configured", message: "Backend 4E nie jest skonfigurowany." },
      "Backend 4E nie jest skonfigurowany.",
    );
  }

  await followupE2EClient.ensureReady(dream?.id || "");
  await syncProfileConsentFromBackend();

  const payload = await build4EFollowupPayload({ dream, analysis, question });
  const response = await fourEClient.followup(payload);
  const backendFollowup = normalizeBackendFollowup({ response, payload, question });

  if (!backendFollowup) {
    throw make4EError(response, "Backend 4E nie zwrócił poprawnej odpowiedzi follow-up.");
  }

  return {
    ok: true,
    source: FOUR_E_SOURCE_BACKEND,
    followup: backendFollowup,
    payload: sanitizePayloadDraft(payload),
    response,
  };
};
